import { createContext, useContext, useCallback, useRef, useState, useEffect } from "react";
import { api, fmtDate } from "@/lib/api";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { WarningIcon as Warning } from "@phosphor-icons/react";

const SuspCtx = createContext(null);

// Promemoria per clienti sospesi per insolvenza: popup bloccante da confermare.
export function SuspendedClientAlertProvider({ children }) {
  const [info, setInfo] = useState(null);
  const lastRef = useRef({ id: null, ts: 0 }); // dedup selezione + guard sullo stesso cliente

  const trigger = useCallback((payload, id) => {
    if (!payload) return;
    const now = Date.now();
    if (lastRef.current.id === id && now - lastRef.current.ts < 2500) return;
    lastRef.current = { id, ts: now };
    setInfo(payload);
  }, []);

  const alertSuspended = useCallback((client) => {
    if (client && client.stato === "sospeso") {
      trigger({
        nome: client.ragione_sociale,
        motivo: client.sospensione_motivo || "Insolvenza",
        sospeso_il: client.sospeso_il,
      }, client.id);
    }
  }, [trigger]);

  const alertSuspendedById = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/clients/${id}`);
      if (data?.stato === "sospeso") {
        trigger({
          nome: data.ragione_sociale,
          motivo: data.sospensione_motivo || "Insolvenza",
          sospeso_il: data.sospeso_il,
        }, id);
      }
    } catch { /* cliente non accessibile: nessun avviso */ }
  }, [trigger]);

  return (
    <SuspCtx.Provider value={{ alertSuspended, alertSuspendedById }}>
      {children}
      <AlertDialog open={!!info} onOpenChange={(o) => { if (!o) setInfo(null); }}>
        <AlertDialogContent data-testid="suspended-client-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <Warning size={22} weight="fill" /> Cliente sospeso per insolvenza
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Il cliente <span className="font-semibold text-foreground">{info?.nome}</span> risulta
                  {" "}<span className="font-semibold text-red-700">sospeso per insolvenza</span>.
                </p>
                <div className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-red-800">
                  <div><span className="text-xs uppercase tracking-wider text-red-600">Motivo</span><br />{info?.motivo}</div>
                  {info?.sospeso_il && <div className="mt-1 text-xs text-red-600">Sospeso il {fmtDate(info.sospeso_il)}</div>}
                </div>
                <p className="text-muted-foreground">Verifica la posizione amministrativa prima di procedere.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="rounded-sm" data-testid="suspended-client-ack">Ho capito</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SuspCtx.Provider>
  );
}

export function useSuspendedAlert() {
  return useContext(SuspCtx) || { alertSuspended: () => {}, alertSuspendedById: () => {} };
}

// Mostra il promemoria quando si apre un documento esistente di un cliente sospeso.
export function useSuspendedClientGuard(clienteId) {
  const { alertSuspendedById } = useSuspendedAlert();
  const checked = useRef(null);
  useEffect(() => {
    if (!clienteId || checked.current === clienteId) return;
    checked.current = clienteId;
    alertSuspendedById(clienteId);
  }, [clienteId, alertSuspendedById]);
}
